import { z } from 'zod';
import { ALL_ROLES, ROLES } from '../constants/roles.js';

const roleSchema = z.enum(ALL_ROLES as [string, ...string[]]);

export const GENDERS = ['male', 'female', 'other', 'prefer_not_to_say'] as const;
export const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'] as const;

const addressSchema = z
  .object({
    street: z.string().trim().max(200).optional().or(z.literal('')),
    city: z.string().trim().max(100).optional().or(z.literal('')),
    state: z.string().trim().max(100).optional().or(z.literal('')),
    country: z.string().trim().max(100).optional().or(z.literal('')),
    pincode: z.string().trim().max(20).optional().or(z.literal('')),
  })
  .partial();

const emergencyContactSchema = z
  .object({
    name: z.string().trim().max(120).optional().or(z.literal('')),
    relationship: z.string().trim().max(60).optional().or(z.literal('')),
    phone: z.string().trim().max(20).optional().or(z.literal('')),
  })
  .partial();

/**
 * Creates a User with an HR profile — "employee" here means any person
 * record in the unified People collection, regardless of their eventual
 * role. `role` is validated as a real role string by this schema, but
 * *whether the caller is allowed to grant it* is a runtime check
 * (`canAssignRole()`) done in the service layer, not something Zod alone
 * can express — see `server/src/services/employee.service.ts`.
 */
export const createEmployeeSchema = z.object({
  name: z.string().trim().min(2, 'Name must be at least 2 characters').max(120),
  email: z.string().trim().toLowerCase().email('Enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters').max(128),
  role: roleSchema.default(ROLES.EMPLOYEE),
  phone: z.string().trim().max(20).optional().or(z.literal('')),
  departmentId: z.string().optional(),
  designation: z.string().trim().max(120).optional().or(z.literal('')),
  reportingTo: z.string().optional(),
  joinDate: z.coerce.date().optional(),
  dateOfBirth: z.coerce.date().optional(),
  gender: z.enum(GENDERS).optional(),
  bloodGroup: z.enum(BLOOD_GROUPS).optional(),
  address: addressSchema.optional(),
  emergencyContact: emergencyContactSchema.optional(),
});

export type CreateEmployeeInput = z.infer<typeof createEmployeeSchema>;

export const updateEmployeeSchema = createEmployeeSchema
  .omit({ password: true })
  .partial()
  .extend({ isActive: z.boolean().optional() });

export type UpdateEmployeeInput = z.infer<typeof updateEmployeeSchema>;

export const employeeListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  search: z.string().trim().max(200).optional(),
  role: roleSchema.optional(),
  departmentId: z.string().optional(),
  isActive: z.coerce.boolean().optional(),
});

export type EmployeeListQuery = z.infer<typeof employeeListQuerySchema>;
